/* eslint camelcase: 0 */
import { Component } from 'preact'
import { Stars } from '../'
import i18n from '../../i18n'
import './Form.scss'
import { getInitialState } from '../../helpers/state'
import Success from './Success'
import { submit } from '../helpers';
import {
  DELAY,
  INITIAL,
  ERROR,
  SUBMITTING,
  SUBMITTED,
  PROMPT_THRESHOLD
} from '../constants'

const CLASS = 'mv-review-form'

const Input = props => <input {...props} id={props.name} /> // eslint-disable-line
const TextArea = props => <textarea {...props} id={props.name} /> // eslint-disable-line
const Label = ({ children, ...props }) => <label {...props}>{children}</label> // eslint-disable-line
const Button = ({ children, ...props }) => <button {...props}>{children}</button> // eslint-disable-line

const InputGroup = ({ error, errors, children }) => (
  <div class={error && errors.some(err => err === error.title) ? 'HasError' : 'NoError'}>
    {children}
  </div>
)

const Error = ({ error, title }) => (error && error.title === title ? <div>{error.details}</div> : null)

class Form extends Component {
  constructor(props) {
    super(props)

    this.initialData = getInitialState(props.id) || {}

    this.state = {
      reviewId: this.initialData.id,
      data: {
        creation: props.id,
        author_name: '',
        author_email: '',
        review_title: '',
        review_content: '',
        ...this.initialData,
        rating: props.rating,
      },
      isAboveThreshold: Number(props.rating) >= PROMPT_THRESHOLD,
      status: INITIAL,
      error: null,
      formIsValid: false,
    }

    this.handleStarClick = this.handleStarClick.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleSubmitButtonClick = this.handleSubmitButtonClick.bind(this)
    this.handleStarSubmit = this.handleStarSubmit.bind(this)
  }

  /**
   * Hanldes form submission on button click.
   */
  async handleSubmitButtonClick(e) {
    e.preventDefault()
    e.target.checkValidity()

    const { data} = this.state
    const formInvalid = this.getFormValidationStatus()

    if (formInvalid) {
      this.setState({ status: ERROR })
      return
    }

    // when the form as a whole is submitted, add/update everything in the form to the review entry
    this.setState({ status: SUBMITTING })
    const res = await submit(data)
    if(res.ok) {
      this.setState({ status: SUBMITTED })
      setTimeout(() => this.props.onSubmit(data), DELAY)
    } else {
      const error = res.data.errors[0]
      this.setState({ status: ERROR, error })
    }
  }

  /**
   * Updates rating and triggers partial submission if the rating is above our threshold.
   */
  handleStarClick(rating) {
    const { id } = this.props

    this.setState(state => ({
      data: { ...state.data, rating },
      isAboveThreshold: Number(rating) >= PROMPT_THRESHOLD,
    }), () => {
      if (this.state.isAboveThreshold) {
        this.handleStarSubmit(id, rating)
      }
    })
  }

  /**
   * Handles partial submission of creation ID and star rating
   */
  async handleStarSubmit(id, rating) {
    // when a star above our threshold is clicked, add/update the star rating to the review entry
    const res = await submit({ creation: id, rating })
    if(!res.ok) {
      this.setState({ status: ERROR })
      return
    }
  }

  /**
   * Updates rating in form.
   */
  handleChange({ target }) {
    const key = target.getAttribute('name')
    const { value } = target
    
    this.setState(state => ({ data: { ...state.data, [key]: value } }))
  }

  getFormValidationStatus() {
    const { data } = this.state

    return !data.rating || (!this.state.isAboveThreshold && !(data.author_name.trim() && data.author_email.trim() && data.review_content))
  }

  render() {
    const { data, isAboveThreshold, status, error } = this.state
    const { author_name, author_email, review_title, review_content, rating } = data // eslint-disable-line

    if (status === SUBMITTED) {
      return (
        <div>
          <div class={`${CLASS}-success`}>
            <Success />
          </div>
          {i18n('THANKS_REVIEW')}
        </div>
      )
    }

    return (
      <form method="get" target="_blank" class={CLASS}
            onSubmit={this.handleSubmitButtonClick}
      >
        <div class={`${CLASS}-stars`}>
          <Stars onClick={this.handleStarClick} rating={rating} />
        </div>

        {status === ERROR && (
          <div>Something went wrong.</div>
        )}


        <InputGroup>
          <Label for="review_title">{i18n('REVIEW_TITLE')}</Label>
          <Input placeholder={i18n('REVIEW_TITLE')} value={review_title} name="review_title" onInput={this.handleChange} />
        </InputGroup>
        <InputGroup error={error} errors={['Message is Required']}>
          <Label for="review_content">{i18n('REVIEW_CONTENT')}{(rating != 0 && !isAboveThreshold) && <span>*</span>}</Label>
          <TextArea placeholder={i18n('REVIEW_CONTENT')} value={review_content} name="review_content" onInput={this.handleChange} required={(rating != 0 && !isAboveThreshold)}/>
          <Error error={error} title="Message is Required" />
        </InputGroup>
        <InputGroup error={error} errors={['Name is Required']}>
          <Label for="author_name">{i18n('Name')}{(rating != 0 && !isAboveThreshold) && <span>*</span>}</Label>
          <Input placeholder={i18n('Name')} value={author_name} name="author_name" onInput={this.handleChange} required={(rating != 0 && !isAboveThreshold)} />
          <Error error={error} title="Name is Required" />
        </InputGroup>
        <InputGroup error={error} errors={['Email is Invalid', 'Email is Required']}>
          <Label for="author_email">{i18n('Email')}{(rating != 0 && !isAboveThreshold) && <span>*</span>}</Label>
          <Input placeholder={i18n('Email')} type="email" value={author_email} name="author_email" onInput={this.handleChange} required={(rating != 0 && !isAboveThreshold)} />
          <Error error={error} title="Email is Invalid" />
          <Error error={error} title="Email is Required" />
        </InputGroup>
        <Button
          type="submit"
          disabled={this.getFormValidationStatus() || status === SUBMITTING}
        >
          {status === SUBMITTING
            ? i18n('Submitting')
            : i18n(this.fromInitial ? 'Update Review' : 'Submit Review')}
        </Button>

        <footer>
          {i18n('CONSENT') /* GDPR consent */}
        </footer>
      </form>
    )
  }
}

export default Form
